import { analyzeSms, toAsciiSafe, formatUsd, COST_PER_SEGMENT_USD } from "@/lib/segments";
import { AlertCircle, Sparkles } from "lucide-react";

/**
 * Live SMS segment + cost meter.
 * - body: current message text
 * - recipients: number of recipients (defaults to 1). Cost = segments × recipients × per-segment rate.
 * - onFix: optional callback(newBody) to auto-clean smart quotes / em-dashes to keep the message in GSM-7 (single segment).
 * - variant: "single" | "broadcast" — broadcast shows total cost across all recipients.
 */
export default function SegmentMeter({ body, recipients = 1, onFix, variant = "single", testId }) {
  const a = analyzeSms(body || "");
  const cleaned = toAsciiSafe(body || "");
  const hasFixableAscii = cleaned !== (body || "") && analyzeSms(cleaned).encoding === "GSM-7";
  const totalCost = a.costUsd * Math.max(1, recipients);
  const willSplit = a.willSplit;
  const isUcs2 = a.encoding === "UCS-2";

  return (
    <div
      className={`text-[11px] flex items-center gap-2 flex-wrap ${willSplit || isUcs2 ? "text-[#D9232E]" : "text-slate-500"}`}
      data-testid={testId || "segment-meter"}
    >
      <span data-testid="segment-chars">{a.chars} chars</span>
      <span className="text-slate-300">·</span>
      <span data-testid="segment-count">
        {a.segments} segment{a.segments === 1 ? "" : "s"}
      </span>
      <span className="text-slate-300">·</span>
      <span title="GSM-7 = 160 chars/segment. UCS-2 (emojis, smart quotes) = only 70 chars/segment.">{a.encoding}</span>
      {variant === "broadcast" && recipients > 0 && (
        <>
          <span className="text-slate-300">·</span>
          <span className="font-semibold text-slate-700" data-testid="segment-total-cost" title={`${a.segments} segment(s) × ${recipients} recipient(s) × ${formatUsd(COST_PER_SEGMENT_USD)}`}>
            ~{formatUsd(totalCost)} to send
          </span>
        </>
      )}
      {variant === "single" && a.segments > 0 && (
        <>
          <span className="text-slate-300">·</span>
          <span className="text-slate-500" data-testid="segment-per-msg-cost">~{formatUsd(a.costUsd)}</span>
        </>
      )}
      {(willSplit || isUcs2) && (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-50 border border-red-200 rounded-full text-[10px] font-semibold" data-testid="segment-warning">
          <AlertCircle className="w-3 h-3" />
          {isUcs2 && !willSplit && "Emoji/quotes = 70 chars per segment"}
          {willSplit && `Will send as ${a.segments}×${recipients > 1 ? " × " + recipients + " people" : ""}`}
        </span>
      )}
      {hasFixableAscii && onFix && (
        <button
          type="button"
          onClick={() => onFix(cleaned)}
          className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 text-emerald-700 rounded-full text-[10px] font-semibold"
          data-testid="segment-fix-button"
          title="Replace smart quotes and em-dashes with plain ASCII to keep this in one segment"
        >
          <Sparkles className="w-3 h-3" /> Auto-clean
        </button>
      )}
    </div>
  );
}
